import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, Clock, Users, Dumbbell, Trophy, ArrowRight } from 'lucide-react';
import { FeaturedLayout } from './layouts';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  Badge,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui';
import { getAgendaHoy } from '../lib/supabase';
import { getMexicoDateString } from '../lib/dateUtils';
import OnboardingChecklist from './OnboardingChecklist';
import AvisosOwner from './avisos/AvisosOwner';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../contexts/OrganizationContext';

const formatFechaLarga = (fecha) => {
  // fecha: 'YYYY-MM-DD'. Formatea sin usar new Date() para datos de negocio.
  const dias = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
  const meses = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
  ];
  const parts = (fecha || '').split('-');
  if (parts.length !== 3) return fecha;
  const [y, m, d] = parts.map((p) => parseInt(p, 10));
  // Calculo de dia de la semana (Zeller-friendly via UTC, sin zona local de negocio).
  const diaSemana = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${dias[diaSemana]} ${d} de ${meses[m - 1]} de ${y}`;
};

const Hoy = () => {
  const navigate = useNavigate();
  const { staff } = useAuth();
  const { org } = useOrganization();
  // Owner = fundador de la org activa (misma regla que PlanComparison y es_owner_de).
  const esOwner = !!staff?.is_founder && staff?.org_id === org?.id;
  const [items, setItems] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [grupoAdHoc, setGrupoAdHoc] = useState('');

  const hoy = getMexicoDateString();

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await getAgendaHoy();
    if (err) {
      setError('No se pudo cargar la agenda de hoy. Intenta de nuevo.');
      setItems([]);
      setGrupos([]);
    } else {
      setItems(data?.items || []);
      setGrupos(data?.grupos || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const entrenamientos = items.filter((i) => i.tipo === 'entrenamiento');
  const juegos = items.filter((i) => i.tipo === 'juego');

  // Grupos para asistencia ad-hoc: excluye los que ya tienen entrenamiento hoy.
  const grupoIdsConEntrenamiento = new Set(entrenamientos.map((e) => e.grupo_id));
  const gruposAdHoc = grupos.filter((g) => !grupoIdsConEntrenamiento.has(g.id));

  const irAEntrenamiento = (grupoId) => {
    navigate(`/attendance/${grupoId}`, {
      state: { mode: 'entrenamiento', fecha: getMexicoDateString() },
    });
  };

  const irAJuego = (grupoId, juegoId) => {
    navigate(`/attendance/juego/${grupoId}/${juegoId}`);
  };

  const irAdHoc = () => {
    if (!grupoAdHoc) return;
    irAEntrenamiento(grupoAdHoc);
  };

  if (loading) {
    return (
      <FeaturedLayout title="Hoy" subtitle={formatFechaLarga(hoy)}>
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Clock className="h-5 w-5 mr-2 animate-spin" />
          Cargando agenda...
        </div>
      </FeaturedLayout>
    );
  }

  return (
    <FeaturedLayout title="Hoy" subtitle={formatFechaLarga(hoy)}>
      <div className="space-y-6">
        <AvisosOwner orgId={org?.id} esOwner={esOwner} />
        <OnboardingChecklist />

        {error && (
          <Card className="border-destructive/40">
            <CardContent className="py-4 flex items-center justify-between gap-3">
              <p className="text-sm text-destructive">{error}</p>
              <Button variant="outline" size="sm" onClick={cargar}>
                Reintentar
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Entrenamientos de hoy */}
        {entrenamientos.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Dumbbell className="h-5 w-5 text-primary" />
              Entrenamientos de hoy
            </h2>
            <div className="space-y-3">
              {entrenamientos.map((item) => (
                <Card key={`ent-${item.id}`}>
                  <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{item.grupo_nombre}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
                        {item.hora && (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {item.hora}
                          </span>
                        )}
                        {item.asistencia_tomada ? (
                          <Badge variant="secondary" className="inline-flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            Asistencia tomada ({item.asistencia_count} registros)
                          </Badge>
                        ) : (
                          <Badge variant="outline">Pendiente</Badge>
                        )}
                      </div>
                    </div>
                    <Button onClick={() => irAEntrenamiento(item.grupo_id)} className="shrink-0">
                      Tomar asistencia
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Juegos de hoy */}
        {juegos.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              Juegos de hoy
            </h2>
            <div className="space-y-3">
              {juegos.map((item) => (
                <Card key={`jue-${item.id}`}>
                  <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{item.grupo_nombre}</p>
                      <p className="text-sm text-muted-foreground truncate">{item.titulo}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
                        {item.hora && (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {item.hora}
                          </span>
                        )}
                        {item.asistencia_tomada ? (
                          <Badge variant="secondary" className="inline-flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            Asistencia tomada ({item.asistencia_count} registros)
                          </Badge>
                        ) : (
                          <Badge variant="outline">Pendiente</Badge>
                        )}
                      </div>
                    </div>
                    <Button onClick={() => irAJuego(item.grupo_id, item.id)} className="shrink-0">
                      Asistencia
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Estado vacio */}
        {items.length === 0 && (
          <Card>
            <CardContent className="py-12 flex flex-col items-center text-center text-muted-foreground">
              <CalendarCheck className="h-10 w-10 mb-3 opacity-60" />
              <p className="font-medium text-foreground">No tienes nada programado hoy</p>
              <p className="text-sm mt-1">
                Puedes tomar asistencia de un grupo cuando lo necesites.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Asistencia ad-hoc de otro grupo */}
        {gruposAdHoc.length > 0 && (
          <section className="space-y-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tomar asistencia de otro grupo</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row sm:items-end gap-3">
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="grupo-adhoc">Grupo</Label>
                  <Select value={grupoAdHoc} onValueChange={setGrupoAdHoc}>
                    <SelectTrigger id="grupo-adhoc">
                      <SelectValue placeholder="Selecciona un grupo" />
                    </SelectTrigger>
                    <SelectContent>
                      {gruposAdHoc.map((g) => (
                        <SelectItem key={g.id} value={g.id}>
                          {g.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={irAdHoc}
                  disabled={!grupoAdHoc}
                  className="shrink-0 inline-flex items-center gap-1"
                >
                  Ir
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </section>
        )}
      </div>
    </FeaturedLayout>
  );
};

export default Hoy;
